/** Orders CREATED in one calendar window, with their outcomes known now. */
export const BUYOUT_COHORT_DAYS = 14;
export const BUYOUT_COHORT_WINDOWS = [BUYOUT_COHORT_DAYS, 30, 60] as const;
export const BUYOUT_COHORT_MIN_RESOLVED = 20;
export type BuyoutCohortWindow = {
  from: string; to: string;
  ordered: number; delivered: number; cancelled: number; returned: number; pending: number;
  returnsKnown: boolean;
};
export type BuyoutCohort = BuyoutCohortWindow & {
  /** Raw equal-calendar windows let models choose a period AFTER pooling sizes. */
  windows?: readonly BuyoutCohortWindow[];
};

export function buyoutCohortDays(cohort: BuyoutCohortWindow): number {
  return Math.round((Date.parse(cohort.to) - Date.parse(cohort.from)) / 86_400_000) + 1;
}

/** Shortest supported period with enough resolved units; otherwise the longest. */
export function selectBuyoutCohort(windows: readonly BuyoutCohortWindow[]): BuyoutCohort {
  if (!windows.length) throw new Error("Buyout cohort requires at least one window");
  const ordered = [...windows].sort((a, b) => buyoutCohortDays(a) - buyoutCohortDays(b));
  const selected = ordered.find(value => value.delivered + value.cancelled >= BUYOUT_COHORT_MIN_RESOLVED) ?? ordered[ordered.length - 1];
  return { ...selected, windows: ordered };
}

export function buyoutCohortRate(cohort: BuyoutCohort | null | undefined): number | null {
  if (!cohort?.returnsKnown) return null;
  const resolved = cohort.delivered + cohort.cancelled;
  return resolved > 0 ? Math.round(Math.max(0, cohort.delivered - cohort.returned) / resolved * 1000) / 10 : null;
}

/** Pool unit counts, never average percentages of models or sizes. */
export function combineBuyoutCohorts(values: readonly (BuyoutCohort | null | undefined)[]): BuyoutCohort | null {
  const first = values[0];
  if (!first || values.some(value => !value)) return null;
  const candidates = first.windows ?? [first];
  const combined: BuyoutCohortWindow[] = [];
  for (const candidate of candidates) {
    const matching = values.map(value => (value!.windows ?? [value!]).find(window => window.from === candidate.from && window.to === candidate.to));
    if (matching.some(value => !value)) continue;
    combined.push(matching.reduce<BuyoutCohortWindow>((total, value) => ({ ...total,
      ordered: total.ordered + value!.ordered, delivered: total.delivered + value!.delivered,
      cancelled: total.cancelled + value!.cancelled, returned: total.returned + value!.returned,
      pending: total.pending + value!.pending, returnsKnown: total.returnsKnown && value!.returnsKnown,
    }), { from: candidate.from, to: candidate.to, ordered: 0, delivered: 0, cancelled: 0, returned: 0, pending: 0, returnsKnown: true }));
  }
  return combined.length ? selectBuyoutCohort(combined) : null;
}

export function buyoutCohortTooltip(cohort: BuyoutCohort | null | undefined): string {
  if (!cohort) return "История исходов заказов ещё не загружена";
  const date = (value: string) => value.split("-").reverse().join(".");
  const resolved = cohort.delivered + cohort.cancelled;
  const sample = resolved < BUYOUT_COHORT_MIN_RESOLVED ? ` Мало данных: нужно хотя бы ${BUYOUT_COHORT_MIN_RESOLVED} завершённых единиц.` : "";
  const details = `За ${buyoutCohortDays(cohort)} дней · завершено ${resolved} шт.${sample} Заказы, оформленные ${date(cohort.from)}–${date(cohort.to)}: ${cohort.ordered} шт. Выкупили ${cohort.delivered}, отменили или отказались ${cohort.cancelled}, вернули после покупки ${cohort.returned}. В пути и незавершённые: ${cohort.pending} — не входят в процент.`;
  if (!cohort.returnsKnown) return `${details} История клиентских возвратов не загружена полностью или возврат нельзя однозначно связать с товаром. Процент пока не рассчитан.`;
  if (cohort.delivered + cohort.cancelled === 0) return `${details} Завершённых заказов пока нет.`;
  return `${details} (${cohort.delivered} − ${cohort.returned}) ÷ (${cohort.delivered} + ${cohort.cancelled}) × 100%. Статусы и поздние возвраты могут изменить результат.`;
}
